/**
 * Sliders body — balanced add/remove helpers
 * For Media Devoted | Created By Ciro Autiero
 *
 * Regression guard for FRM-sliders-weight-desync: rows used to be appended
 * with `weight: 0` and only healed on drag, so the persisted draft carried
 * zeros while the auto-balanced UI displayed an equal split. These helpers
 * keep the draft body in lockstep with the balanced UI — every add/remove
 * writes the same integer weights (summing to 100) the sliders render.
 */

import type { FormulaSubMetric } from '@trafficpulse/shared';

import { rebalance } from '../../../hooks/auto-balanced-sliders.math';

let counter = 0;
function generateMetricId(): string {
  return `sm_${Date.now().toString(36)}_${counter++}`;
}

/** Append an empty row seeded with an equal share of the balanced weights. */
export function appendMetricBalanced(
  metrics: FormulaSubMetric[],
): FormulaSubMetric[] {
  const id = generateMetricId();
  const displayed = rebalance(weightsOf(metrics), null);
  const share = 100 / (metrics.length + 1);
  const seeded: Record<string, number> = { [id]: share };
  for (const m of metrics) {
    seeded[m.id] = (displayed[m.id] ?? 0) * ((100 - share) / 100);
  }
  const next = [...metrics, { id, metric: '', weight: 0 }];
  return applyWeights(next, rebalance(seeded, null));
}

/** Drop a row and rebalance the remaining weights back to a 100 total. */
export function removeMetricBalanced(
  metrics: FormulaSubMetric[],
  id: string,
): FormulaSubMetric[] {
  const remaining = metrics.filter((m) => m.id !== id);
  if (remaining.length === 0) return remaining;
  return applyWeights(remaining, rebalance(weightsOf(remaining), null));
}

function weightsOf(metrics: FormulaSubMetric[]): Record<string, number> {
  return Object.fromEntries(metrics.map((m) => [m.id, m.weight]));
}

function applyWeights(
  metrics: FormulaSubMetric[],
  weights: Record<string, number>,
): FormulaSubMetric[] {
  return metrics.map((m) => ({ ...m, weight: weights[m.id] ?? m.weight }));
}
